import { isBoolean } from 'lodash-es'

import { LooseOctree } from '../extras/LooseOctree'

import * as THREE from '../three'

import { System } from './System'

export class GraphicsSystem extends System {
  constructor(client) {
    super(client)
  }

  async init({ viewport }) {
    this.viewport = null
    this.width = 1 // this.viewport.offsetWidth
    this.height = 1 // this.viewport.offsetHeight
    this.aspect = this.width / this.height
    this.scene = new THREE.Scene()
    this.octree = new LooseOctree({
      scene: this.scene,
      center: new THREE.Vector3(0, 0, 0),
      size: 10,
    })
    this.supportsVR = await navigator.xr?.isSessionSupported('immersive-vr')
    this.session = null
    this.camera = new THREE.PerspectiveCamera(70, 0, 0.05, 600)
    this.renderer = new THREE.WebGLRenderer({
      powerPreference: 'high-performance',
      antialias: true,
      // precision: 'highp',
      // outputColorSpace: THREE.SRGBColorSpace,
      // alpha: false,
      // logarithmicDepthBuffer: true,
      // reverseDepthBuffer: true,
    })
    this.renderer.setSize(this.width, this.height)
    this.renderer.setClearColor(0xffffff, 0)
    this.renderer.setPixelRatio(window.devicePixelRatio) // not used in vr
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.toneMapping = THREE.NoToneMapping // THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.xr.enabled = true
    this.renderer.xr.setReferenceSpaceType('local-floor')
    this.maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy()
    THREE.Texture.DEFAULT_ANISOTROPY = this.maxAnisotropy
  }

  setViewport(viewport) {
    if (this.viewport) {
      this.viewport.removeChild(this.renderer.domElement)
      this.viewport = null
      this.resizer.disconnect()
    }
    this.viewport = viewport
    this.viewport.appendChild(this.renderer.domElement)
    this.resize(viewport.offsetWidth, viewport.offsetHeight) // trigger immediate (resize observer first trigger is async and we dont want that)
    let hasInit
    this.resizer = new ResizeObserver(() => {
      if (!hasInit) {
        hasInit = true
        return
      }
      this.resize(this.viewport.offsetWidth, this.viewport.offsetHeight)
    })
    this.resizer.observe(this.viewport)
    this.emit('viewport', this.viewport)
  }

  start() {
    this.renderer.setAnimationLoop(this.client.tick)
  }

  resize(width, height) {
    this.width = width
    this.height = height
    this.aspect = this.width / this.height
    this.camera.aspect = this.aspect
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(this.width, this.height)
    this.emit('resize')
    this.render()
  }

  update(delta) {
    this.render()
  }

  render() {
    this.renderer.render(this.scene, this.camera)
  }

  async enterVR() {
    if (this.session) return
    this.renderer.xr.setFoveation(1)
    // this.renderer.samples = 4 // Multisample antialiasing
    this.renderer.xr.setFramebufferScaleFactor(1.3) // Or higher, up to ~1.5
    const session = await navigator.xr?.requestSession('immersive-vr', {
      requiredFeatures: ['local-floor'],
    })
    // try {
    //   session.updateTargetFrameRate(72)
    // } catch (err) {
    //   console.error(err)
    //   console.error('xr session.updateTargetFrameRate(72) failed')
    // }
    this.renderer.xr.setSession(session)
    this.session = session
    this.client.emit('xrSession', session)
    session.addEventListener('end', () => {
      this.session = null
      this.client.emit('xrSession', null)
    })
  }

  leaveVR() {
    if (!this.session) return
    this.session.end()
    this.session = null
    this.client.emit('xrSession', null)
  }

  toggleWireframe(enabled) {
    enabled = isBoolean(enabled) ? enabled : !this.wireframe
    if (this.wireframe === enabled) return
    this.wireframe = enabled
    this.scene.traverse(obj => {
      if (!obj.isMesh || !obj.material) return
      const apply = mat => {
        if (!mat || typeof mat.wireframe !== 'boolean') return
        // remember original once
        if (!Object.prototype.hasOwnProperty.call(mat.userData, '_wireframe0')) {
          mat.userData._wireframe0 = mat.wireframe
        }
        mat.wireframe = enabled
        mat.needsUpdate = true
      }
      if (Array.isArray(obj.material)) obj.material.forEach(apply)
      else apply(obj.material)
    })
  }
}
